import { Helper } from "../../../Helper";
import * as React from 'react';
import { Environment } from '../../../services/Environment';
import * as styles from "./MessageVideo.css";
import { IEnvironment } from "../../../interfaces/IEnvironment";

export interface IMessageVideoProps {
  id?: string;
  width: string | number;
  height: string | number;
  videoUrl: string;
  environment: IEnvironment;
  tenant: string;
  token: string;
  onPlayVideo?: () => void;
  onPause?: () => void;
  isPlaying?: boolean;
  title?: string;
  forceStop?: boolean;
  style?: React.CSSProperties;
  fixSmallYTThumbnail?: boolean;
  thumbnailUrl?: string;
  backgroundColor?: string;
  showControls?: boolean;
  onUnsupportedVideo?: () => void;
}

export interface IMessageVideoState {
  videoUrl: string;
  videoType: VideoType;
  isPlaying: boolean;
  randomId: string;
  hasStarted: boolean;
  videoRef: React.RefObject<HTMLVideoElement>;
}

export class IAMessageVideo extends React.Component<IMessageVideoProps, IMessageVideoState> {
  constructor(props: IMessageVideoProps) {
    super(props);
    this.state = {
      videoUrl: undefined,
      videoType: undefined,
      isPlaying: !!props.isPlaying, // honor autoplay requested from the very first mount, not just later prop changes
      randomId: undefined,
      hasStarted: false,
      videoRef: React.createRef<HTMLVideoElement>()
    }
  }

  componentDidMount(): void {
    this.setState({ randomId: Helper.getRandomStringKey() });
    this.setUrlAndType();
  }

  componentDidUpdate(prevProps: Readonly<IMessageVideoProps>): void {
    if ((this.props.isPlaying !== prevProps.isPlaying && !this.props.isPlaying) || (this.props.forceStop !== prevProps.forceStop && this.props.forceStop)) {
      this.setState({ isPlaying: false });
      this.resetIframe();
      if (this.state.videoRef.current) {
        this.state.videoRef.current.pause();
      }
    }
    if (this.props.isPlaying !== prevProps.isPlaying && this.props.isPlaying) {
      this.setState({ isPlaying: true });
      if (this.state.videoRef.current) {
        this.state.videoRef.current.play();
      }
    }
    if (this.props.videoUrl !== prevProps.videoUrl) {
      this.setUrlAndType();
    }
  }

  private onPlay(): void {
    if (this.props.onPlayVideo) {
      this.props.onPlayVideo();
    }
    this.setState({ isPlaying: true, hasStarted: true });
  }

  private onPause(): void {
    if (this.props.onPause) {
      this.props.onPause();
    }
    this.setState({ isPlaying: false });
  }

  private resetIframe(): void {
    var videoIframe = document.getElementById(`IA_Iframe_${this.props.id}_${this.state.randomId}`) as HTMLIFrameElement;
    if (videoIframe !== null) {
      const query = this.state.videoUrl.indexOf("?") !== 1 ? "&" : "?";
      videoIframe.src = this.state.videoUrl + `${query}_=${new Date().getTime()}`;
    }
    this.onPause();
  }

  private setUrlAndType(): void {
    const videoType = this.getVideoType();
    this.setState({ videoType: videoType }, () => this.setState({ videoUrl: this.getUrl() }))
  }

  private getVideoType(): VideoType {
    if (!this.props.videoUrl) {
      return "notValid";
    }
    const url = this.props.videoUrl.toLocaleLowerCase();
    if (url.indexOf("microsoftstream") === -1 && url.indexOf("sharepoint") === -1) {
      if (Helper.isYouTubeUrl(url)) {
        return "youtube";
      } else if (Helper.isVimeoVideo(url)) {
        return "vimeo";
      } else {
        return "iframe";
      }
    } else if (url.includes("stream") && url.includes("embed")) {
      return "stream";
    } else if (Helper.isMp4Video(url)) {
      return "mp4";
    } else if (Helper.isMp3Audio(url)) {
      return "mp3";
    } else if (url.indexOf(".webm") !== -1 || url.indexOf(".mov") !== -1) {
      return "webm/mov";
    } else {
      if (!!this.props.onUnsupportedVideo) this.props.onUnsupportedVideo();
      return "notValid";
    }
  }

  private getUrl(): string {
    if (!this.props.videoUrl) {
      return;
    }
    const isPlaying = !!this.props.isPlaying;
    let videoUrl = this.props.videoUrl?.replace(/autoPlay=[01]/, `autoPlay=${isPlaying ? 1 : 0}`);
    if (this.state.videoType === "stream") {
      videoUrl = Helper.hideStreamInfo(videoUrl);
    }
    if (this.state.videoType === "mp4" || this.state.videoType === "mp3" || this.state.videoType === "webm/mov") {
      videoUrl = Helper.getVideoUrl(Environment.getEnvironmentForImageAPI(this.props.environment), this.props.tenant, this.props.token, videoUrl) + "#t=0.1";
    }
    if (this.state.videoType === "youtube") {
      // Embed codes often already contain their own autoplay=0 - appending another autoplay param
      // creates a duplicate query key that YouTube resolves using the first (stale) value, so the
      // URL API is used to actually replace it rather than just string-concatenating a new one.
      try {
        const parsedUrl = new URL(videoUrl);
        parsedUrl.searchParams.set("modestbranding", "1");
        parsedUrl.searchParams.set("rel", "0");
        parsedUrl.searchParams.set("showinfo", "0");
        parsedUrl.searchParams.set("autoplay", isPlaying ? "1" : "0");
        // Browsers block unmuted autoplay, so YouTube silently ignores autoplay=1 without mute=1 too
        if (isPlaying) {
          parsedUrl.searchParams.set("mute", "1");
        } else {
          parsedUrl.searchParams.delete("mute");
        }
        videoUrl = parsedUrl.toString();
      } catch (_urlError) {
        const separator = videoUrl.indexOf("?") !== -1 ? "&" : "?";
        videoUrl = videoUrl + `${separator}modestbranding=1&rel=0&showinfo=0&autoplay=${isPlaying ? 1 : 0}${isPlaying ? "&mute=1" : ""}`;
      }
    }
    if ((this.state.videoType === "vimeo" || this.state.videoType === "iframe") && isPlaying) {
      try {
        const parsedUrl = new URL(videoUrl);
        // Facebook's video plugin uses word-booleans (not 1/0) and needs muting to get past the browser's autoplay-with-sound block
        if (parsedUrl.hostname.includes("facebook.com") && parsedUrl.pathname.includes("/plugins/video.php")) {
          parsedUrl.searchParams.set("autoplay", "true");
          parsedUrl.searchParams.set("mute", "1");
        } else {
          parsedUrl.searchParams.set("autoplay", "1");
        }
        videoUrl = parsedUrl.toString();
      } catch (_urlError) {
        if (!videoUrl.includes("autoplay=")) {
          const separator = videoUrl.indexOf("?") !== -1 ? "&" : "?";
          videoUrl = videoUrl + `${separator}autoplay=1`;
        }
      }
    }
    return videoUrl;
  }

  public render(): JSX.Element {
    if (this.state.videoType === "notValid") {
      return <></>;
    } else {
      const style = this.props.style ?? {};
      return (
        <div
          id={`IA_MessageVideo_${this.props.id}`}
          style={{
            ...style,
            height: this.props.height,
            width: this.props.width,
            backgroundColor: this.props.backgroundColor ?? "black"
          }}
          className={styles.IA_VideoMainDiv}
        >
          {(this.state.videoType === "mp4" || this.state.videoType === "mp3" || this.state.videoType === "webm/mov") &&
            <video
              ref={this.state.videoRef}
              id={`IA_MessageVideo_${this.props.id}_${this.state.randomId}`}
              width={this.props.width}
              height={this.props.height}
              src={this.state.videoUrl}
              autoPlay={this.state.isPlaying}
              controls={this.props.showControls ?? true}
              poster={this.props.thumbnailUrl}
              disablePictureInPicture
              controlsList="nodownload noplaybackrate nofullscreen"
              onPlay={() => this.onPlay()}
              onPause={() => this.onPause()}
              onLoadedMetadata={() => {
                if (this.state.isPlaying && !this.props.forceStop && this.state.videoRef.current) {
                  this.state.videoRef.current.play();
                }
              }}
              style={{
                objectFit: 'contain',
                backgroundColor: this.props.backgroundColor ?? "black"
              }}
            />
          }
          {(this.state.videoType === "stream" || this.state.videoType === "youtube" || this.state.videoType === "iframe" || this.state.videoType === "vimeo") &&
            <iframe id={`IA_Iframe_${this.props.id}_${this.state.randomId}`}
              width={this.props.width}
              height={this.props.height}
              allowFullScreen
              allow={this.props.isPlaying ? "autoplay; encrypted-media; picture-in-picture" : undefined}
              src={this.state.videoUrl}
              style={{
                float: this.props.style?.float ?? "none",
                border: "none",
                backgroundColor: this.props.backgroundColor ?? "black"
              }}
            />
          }
        </div>
      );
    }
  }
}

export type VideoType = "youtube" | "vimeo" | "iframe" | "mp4" | "webm/mov" | "stream" | "mp3" | "notValid";